import React from 'react';
import { TrendingUp, Target, Zap } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { sectorDetailData } from '../../../data/sectorData';

const getSignalStyle = (signal: string): string => {
  const styles: Record<string, string> = {
    'STRONG BUY': 'bg-data-green text-black font-bold',
    'BUY': 'bg-data-green/70 text-black font-semibold',
    'HOLD': 'bg-data-amber text-black font-semibold',
    'SELL': 'bg-data-red text-white font-semibold',
  };
  return styles[signal] || 'bg-gray-600 text-white';
};

const getMaturityStyle = (maturity: string): string => {
  const styles: Record<string, string> = {
    'NASCENT': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'EARLY': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'GROWTH': 'bg-data-green/20 text-data-green border-data-green/30',
    'SCALING': 'bg-data-blue/20 text-data-blue border-data-blue/30',
  };
  return styles[maturity] || 'bg-gray-600/20 text-gray-400 border-gray-500/30';
};

interface SectorOverviewPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const SectorOverviewPanel: React.FC<SectorOverviewPanelProps> = ({ isMinimized, onMinimize }) => {
  const { comparison } = sectorDetailData;
  const sortedSectors = [...comparison].sort((a, b) => b.score - a.score);
  const totalTAM2024 = comparison.reduce((sum, s) => sum + s.tam2024, 0);
  const totalTAM2028 = comparison.reduce((sum, s) => sum + s.tam2028, 0);
  const strongBuys = comparison.filter(s => s.signal === 'STRONG BUY').length;
  const avgScore = Math.round(comparison.reduce((sum, s) => sum + s.score, 0) / comparison.length);

  return (
    <Panel title="SECTOR MATRIX" subtitle="SIGNALS" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-amber">${totalTAM2028.toFixed(1)}B</div>
            <div className="text-xs text-gray-500">TAM 2028</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-green">+{(((totalTAM2028 - totalTAM2024) / totalTAM2024) * 100).toFixed(0)}%</div>
            <div className="text-xs text-gray-500">Growth</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-white">{strongBuys}</div>
            <div className="text-xs text-gray-500">Strong Buys</div>
          </div>
        </div>

        {/* Sector Cards */}
        <div className="flex-1 space-y-2.5 overflow-auto custom-scrollbar">
          {sortedSectors.map((s, idx) => (
            <div
              key={s.sector}
              className="bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-white">{s.sector}</span>
                  <span className={`text-xs px-2 py-0.5 rounded border ${getMaturityStyle(s.maturity)}`}>
                    {s.maturity}
                  </span>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded ${getSignalStyle(s.signal)}`}>
                  {s.signal}
                </span>
              </div>

              {/* Score Bar */}
              <div className="h-2 bg-terminal-border rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full rounded-full ${s.score >= 90 ? 'bg-data-green' : s.score >= 80 ? 'bg-data-amber' : 'bg-data-blue'}`}
                  style={{ width: `${s.score}%` }}
                />
              </div>

              {/* Metrics Row */}
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">TAM:</span>
                  <span className="font-mono text-white">${s.tam2024.toFixed(1)}B</span>
                  <span className="text-gray-500">→</span>
                  <span className="font-mono text-data-amber">${s.tam2028.toFixed(1)}B</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono font-bold ${s.cagr >= 50 ? 'text-data-green' : 'text-data-amber'}`}>
                    +{s.cagr}% CAGR
                  </span>
                  <span className="text-lg font-mono font-black text-white">{s.score}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default SectorOverviewPanel;
